// Where we found it: the place a finding was seen, in the user's words. Separate from Category, which says what a
// finding puts at risk and drives the score (a Ticketmaster breach is Category money, but we found it in a data breach).
// Places: data breaches (dark web) · your transactions · social profiles · people-search sites · public records.
import { LADDER, PLANS } from './entitlements';
import type { Finding, Plan, SourceId } from './types';

export type Place = 'breaches' | 'transactions' | 'profiles' | 'people_search' | 'public_records';
export const PLACES: Place[] = ['breaches', 'transactions', 'profiles', 'people_search', 'public_records'];

// Which source finds each place.
export const PLACE_SOURCE: Record<Place, SourceId> = {
  breaches: 'dark_web', transactions: 'transactions', profiles: 'osint', people_search: 'osint', public_records: 'osint',
};

// Official registers (company, electoral, court) are public records; every other address or listing site is people search.
const RECORD = /\b(record|register|registry|electoral|court|gazette)\b/i;

export function placeOf(f: Pick<Finding, 'source' | 'category' | 'title'>): Place {
  if (f.source === 'transactions') return 'transactions';
  if (f.source !== 'osint') return 'breaches';
  if (f.category === 'public_profiles') return 'profiles';
  if (RECORD.test(f.title)) return 'public_records';
  return 'people_search';
}

export const isPlaceScanned = (plan: Plan, p: Place) => PLANS[plan].entitlements.sources.includes(PLACE_SOURCE[p]);
export const planForPlace = (p: Place): Plan =>
  LADDER.find((x) => x !== 'free_scan' && PLANS[x].entitlements.sources.includes(PLACE_SOURCE[p])) ?? 'complete';

// Paywall entry point for a place the plan does not scan (reuses the map entry points and their headlines).
export const PLACE_ENTRY: Record<Place, string> = {
  breaches: 'map_not_scanned_dark_web', transactions: 'map_not_scanned_money', profiles: 'map_not_scanned_public_profiles',
  people_search: 'map_not_scanned_data_brokers', public_records: 'map_not_scanned_location',
};

// What it exposes: the kinds of personal data a finding shows, from its exposed fields. A second lens next to place.
export type Exposed = 'password' | 'email' | 'phone' | 'address' | 'identity' | 'card' | 'username' | 'activity';
export const EXPOSED: Exposed[] = ['password', 'card', 'address', 'phone', 'email', 'identity', 'username', 'activity'];
const FIELD_KIND: [RegExp, Exposed][] = [
  [/password/i, 'password'], [/card|amount|iban|account/i, 'card'], [/address/i, 'address'], [/phone/i, 'phone'],
  [/e-?mail/i, 'email'], [/user ?name/i, 'username'], [/name|age|birth/i, 'identity'], [/visible|photo|post|route/i, 'activity'],
];
export function exposesOf(f: Pick<Finding, 'exposedFields'>): Exposed[] {
  const out = new Set<Exposed>();
  for (const x of f.exposedFields) {
    const hit = FIELD_KIND.find(([re]) => re.test(x.label));
    if (hit) out.add(hit[1]);
  }
  return EXPOSED.filter((k) => out.has(k));
}
